// One redemption, however the member arrived at it.
//
// Two entry points need the same sequence — the deeplink page (a card scanned
// with the phone's own camera, which is how cards are actually used) and the
// in-app scanner. They render completely differently: one is a full screen with
// a spinner and a "Go to course" button, the other is a toast over a camera
// preview. What must NOT differ is the decision-making, so it lives here and
// the two callers only choose how to say it.

import { normalizeIdrCode, isCollectionPass } from '@/utils/idr-code';
import { REDEEM_MESSAGES, redeemErrorMessage } from '@/utils/redeem-messages';

export const REDEEM_OUTCOME = {
  UNLOCKED: 'unlocked',
  NEEDS_CG: 'needs-cg',
  REFUSED: 'refused',
};

/**
 * Turn whatever was scanned or typed into a code worth sending, or a reason not
 * to. Refusals here never reach the network: a string that breaks IDR's own
 * printing rules cannot be a card they minted.
 *
 * @param {string} raw
 * @returns {{ code: string } | { error: string }}
 */
export function prepareCode(raw) {
  const code = normalizeIdrCode(raw || '');
  if (code) return { code };

  return {
    error: isCollectionPass(raw) ? REDEEM_MESSAGES.COLLECTION_PASS : REDEEM_MESSAGES.INVALID_CODE,
  };
}

/**
 * Send a prepared code and classify the answer. Never throws — every failure
 * comes back as a REFUSED outcome carrying words a member can read.
 *
 * @param {object} store Vuex store
 * @param {string} code Canonical code from prepareCode
 * @returns {Promise<{outcome: string, programSlug?: string, replay?: boolean, message?: string}>}
 */
export async function submitCode(store, code) {
  let result;
  try {
    result = await store.dispatch('courses/redeemBookCode', { code });
  } catch (error) {
    return { outcome: REDEEM_OUTCOME.REFUSED, message: redeemErrorMessage(error) };
  }

  // The server can refuse to grant until this member has a main CG. There is no
  // program to navigate to in that answer — the caller hands the code over and
  // the CG block finishes the redemption once it is answered.
  if (result?.needsCgLink) {
    return { outcome: REDEEM_OUTCOME.NEEDS_CG, message: REDEEM_MESSAGES.NEEDS_CG_LINK };
  }

  // Mock mode, and any 200-with-an-error-body, report failure in the payload
  // rather than the status.
  if (result?.error) {
    return { outcome: REDEEM_OUTCOME.REFUSED, message: result.error };
  }

  const programSlug = result?.unlockedProgramSlug;
  if (!programSlug) {
    return { outcome: REDEEM_OUTCOME.REFUSED, message: REDEEM_MESSAGES.DEFAULT };
  }

  return { outcome: REDEEM_OUTCOME.UNLOCKED, programSlug, replay: !!result.replay };
}
